// Pure helpers for the app shell (kept apart from components so fast refresh stays happy).
import type { Location } from 'react-router'
import type { MeCommunity } from '@/lib/auth'
import type { Community, CommunityTabKey } from '@/lib/community'
import { t } from '@/lib/i18n'
import type { ModalLocationState } from './types'

/** Search type and placeholder per community tab (SHELL-004). */
export function searchContext(slug: string | null, tab: CommunityTabKey | null): { placeholder: string; type?: string } {
  if (!slug) return { placeholder: t('Search MG AI School') }
  switch (tab) {
    case 'members':
    case 'map':
    case 'leaderboards':
      return { placeholder: t('Search members'), type: 'members' }
    case 'classroom':
      return { placeholder: t('Search courses'), type: 'courses' }
    case 'calendar':
      return { placeholder: t('Search events'), type: 'events' }
    default:
      return { placeholder: t('Search posts'), type: 'posts' }
  }
}

/**
 * Where Enter goes: /:slug/search?q= inside a community. Search only exists inside a community (one creator, no
 * directory; `/discover` is gone), so without a slug it falls back to the home page (Q-TRUST-11).
 */
export function searchHref(slug: string | null, q: string, type?: string): string {
  if (!slug) return '/'
  const params = new URLSearchParams({ q })
  if (type && type !== 'posts') params.set('type', type)
  return `/${slug}/search?${params}`
}

/** A community card from MG's published list (subset of the discover card shape the switcher needs). */
export type SwitcherSuggestion = {
  id: string
  slug: string
  name: string
  iconUrl: string | null
  brandColor: string | null
}

/** MG's communities the viewer can still join: everything in the list they have no membership row for. */
export function joinableCommunities(
  list: SwitcherSuggestion[] | undefined,
  mine: Pick<MeCommunity, 'slug'>[] | undefined,
): SwitcherSuggestion[] {
  const joined = new Set((mine ?? []).map((c) => c.slug.toLowerCase()))
  const seen = new Set<string>()
  return (list ?? []).filter((c) => {
    const key = c.slug.toLowerCase()
    if (joined.has(key) || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** Joined communities for the switcher: pinned first, then the saved order; hidden ones excluded. */
export function switcherCommunities(list: MeCommunity[] | undefined): MeCommunity[] {
  return (list ?? [])
    .filter((c) => !c.hiddenInSwitcher && (c.status === 'active' || c.status === 'pending'))
    .slice()
    .sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
      const pa = a.switcherPosition ?? Number.MAX_SAFE_INTEGER
      const pb = b.switcherPosition ?? Number.MAX_SAFE_INTEGER
      // Equal positions keep the API order (Array.sort is stable), the same order Settings, Communities lists,
      // instead of a name tie break that disagreed with it until the member dragged (Q-COMM-30).
      return pa - pb
    })
}

/** Which tab a path belongs to: posts belong to Community, a course to Classroom. Null for search, settings. */
export function activeTabFor(pathname: string, slug: string): CommunityTabKey | null {
  const base = `/${slug.toLowerCase()}`
  const p = pathname.toLowerCase().replace(/\/+$/, '')
  if (p === base || p.startsWith(`${base}/post/`)) return 'community'
  const sub = p.slice(base.length + 1).split('/')[0]
  const tabs: CommunityTabKey[] = ['classroom', 'calendar', 'members', 'map', 'leaderboards', 'about']
  return (tabs as string[]).includes(sub) ? (sub as CommunityTabKey) : null
}

/**
 * False only for a private community whose content the viewer cannot see (visitors, non members, pending
 * requests). Unknown while loading reads as true, so public communities never flicker.
 */
export function canBrowseCommunity(
  community: Community | null | undefined,
  opts: { isMember: boolean; isPlatformAdmin: boolean },
): boolean {
  if (!community || community.privacy !== 'private') return true
  return community.viewer?.canViewContent ?? (opts.isMember || opts.isPlatformAdmin)
}

/** True when a visitor must be sent to About (SHELL-011): private community without access. /<slug>/plans is not
 * sent: its page goes to About itself with the plan picker open and ?tier= kept (paid-admin-2, MEMBERS-09). */
export function mustRedirectToAbout(
  community: Community | null,
  opts: { isMember: boolean; isPlatformAdmin: boolean; pathname: string; slug: string },
): boolean {
  if (canBrowseCommunity(community, opts)) return false
  return activeTabFor(opts.pathname, opts.slug) !== 'about' && !isPlansPath(opts.pathname, opts.slug)
}

/** /<slug>/plans, the plans address (MEMBERS-09). */
export function isPlansPath(pathname: string, slug: string): boolean {
  return pathname.toLowerCase().replace(/\/+$/, '') === `/${slug.toLowerCase()}/plans`
}

/**
 * Banned viewers get the full page "no longer have access" state on every tab except About (UI 7).
 * me.communities may omit banned rows, so the community payload's viewer status counts too.
 */
export function showBannedState(
  community: Community | null,
  opts: { bannedInMe: boolean; pathname: string; slug: string },
): boolean {
  const banned = opts.bannedInMe || community?.viewer?.status === 'banned'
  return banned && activeTabFor(opts.pathname, opts.slug) !== 'about'
}

/** Sub paths (below /:slug) that open as a modal when a background location is present. */
const MODAL_PATHS = [/^\/[^/]+\/post\/[^/]+\/?$/, /^\/[^/]+\/settings(\/[^/]+)?\/?$/]

export function isModalPath(pathname: string): boolean {
  return MODAL_PATHS.some((re) => re.test(pathname))
}

/** The background location of a modal route, when it belongs to the same community. */
export function backgroundFor(location: Location, slug: string): Location | undefined {
  const bg = (location.state as ModalLocationState | null)?.backgroundLocation
  if (!bg || !isModalPath(location.pathname)) return undefined
  const base = `/${slug.toLowerCase()}`
  const p = bg.pathname.toLowerCase()
  return p === base || p.startsWith(base + '/') ? bg : undefined
}


/**
 * Scroll restoration key. Opening a modal route keeps the scroll position of the page behind it. Every full page
 * load starts on the history key "default", so those entries are told apart by their URL (E2E-J10-04): otherwise a
 * new page loaded in the same tab (a pasted link, a push notification tap in the installed app) is scrolled to where
 * the previous page of the tab was, and its #section anchor is ignored. A reload keeps its own position.
 */
export function scrollKey(location: Location): string {
  const shown = (location.state as ModalLocationState | null)?.backgroundLocation ?? location
  return shown.key === 'default' ? `default:${shown.pathname}${shown.search}${shown.hash}` : shown.key
}
